import { CustomToastComponent } from "@/components/shared";
import { Document, DocumentStatus } from "@/graphql/__generated__/graphql";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RemoveDocumentFromKnowledgeBaseMutation,
  RemoveDocumentFromKnowledgeBaseMutationVariables,
  RemoveDocumentFromKnowledgeBaseDocument,
  FetchDocumentsByTenderIdDocument,
} from "@/graphql/__generated__/graphql";

/**
 * Custom hook for removing a document from the knowledge base and handling the necessary cache updates.
 * @returns {Object} - Contains the RemoveDocumentFromKnowledgeBase function, loading state, and error state.
 */
export default function useRemoveDocumentFromKnowledgeBaseMutationHook(
  setMutationPollingActive: Dispatch<SetStateAction<boolean>>
): { RemoveDocumentFromKnowledgeBase: any; loading: any; error: any } {
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [refetchDocumentsByTenderId] = useLazyQuery(FetchDocumentsByTenderIdDocument, {
    fetchPolicy: "network-only"
  });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const [RemoveDocumentFromKnowledgeBase, { loading, error }] = useMutation<RemoveDocumentFromKnowledgeBaseMutation, RemoveDocumentFromKnowledgeBaseMutationVariables>(
    RemoveDocumentFromKnowledgeBaseDocument, {
      onCompleted: async (data) => {
        const documentId = data.removeDocumentFromKnowledgeBase?.id;
        if (!documentId) return;

        const mutationPollingCompleted = () => {
          setMutationPollingActive(false);
        };

        setMutationPollingActive(true);

        const pollInterval = 10000; // 10 seconds
        const maxTimeout = 10 * 60 * 1000; // 10 minutes maximum
        const startTime = Date.now();

        const pollForUpdates = async () => {
          if (Date.now() - startTime > maxTimeout) {
            console.error("Timeout reached while removing document from knowledge base");
            mutationPollingCompleted();
            return;
          }

          const result = await refetchDocumentsByTenderId({
            variables: { tenderId: Number(tenderID) }
          });

          const documents = result.data?.fetchDocumentsByTenderId || [];
          const targetDocument = documents.find((doc: Document) => doc.id === documentId) as Document;

          if (targetDocument) {
            if (targetDocument.status === DocumentStatus.NotInKnowledgeBase) {
              toast(CustomToastComponent, {
                toastId: "document-kb-remove-success",
                data: {
                  title: t("toasts.documentremovedfromknowledgebasesuccessfully"),
                  type: "success",
                }
              });
              mutationPollingCompleted();
              return;
            } else if (targetDocument.status === DocumentStatus.InKnowledgeBase) {
              toast(CustomToastComponent, {
                toastId: "document-kb-remove-failed",
                data: {
                  title: t("toasts.documentremovedfromknowledgebasefailed"),
                  type: "error",
                }
              });
              mutationPollingCompleted();
              return;
            } else if (targetDocument.status === DocumentStatus.RemovingFromKnowledgeBase) {
            // Clear any existing timeout before setting a new one
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              timeoutRef.current = setTimeout(pollForUpdates, pollInterval);
            }
          } else {
            mutationPollingCompleted();
            return;
          }
        };

        pollForUpdates();
      }
    });

  return {
    RemoveDocumentFromKnowledgeBase,
    loading,
    error,
  };
}
